/**
 * ErrorTrackingService — web wrapper around @sentry/react.
 *
 * Exposes the SAME public API surface that the ported sync engine and
 * other ported modules call (captureException, captureMessage,
 * addBreadcrumb, setUser, setStore, startTransaction, setSyncContext).
 *
 * Sentry init is gated on a non-empty DSN. When the DSN is empty
 * (default in dev), every method becomes a console-only no-op so the
 * UI never depends on a remote service being reachable.
 */
import * as Sentry from '@sentry/react';
import { SENTRY_DSN, IS_DEV } from '../config';

let initialized = false;

export function initErrorTracking(): void {
  if (initialized) return;
  initialized = true;
  if (!SENTRY_DSN) {
    if (IS_DEV) console.log('[ErrorTracking] No DSN — running in console-only mode.');
    return;
  }
  // Renderer-side Sentry; main-process init lives in electron/main.ts when wired.
  Sentry.init({ dsn: SENTRY_DSN });
}

export interface UserContext {
  id: string;
  phone?: string;
  role?: string;
  merchantId?: string;
}

export function setUser(user: UserContext | null): void {
  if (!SENTRY_DSN) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id });
  Sentry.setTag('user.role', user.role ?? 'unknown');
  Sentry.setTag('user.merchantId', user.merchantId ?? 'unknown');
}

export interface StoreContext {
  storeId: string;
  storeName?: string;
}

export function setStore(store: StoreContext | null): void {
  if (!SENTRY_DSN) return;
  if (store) {
    Sentry.setTag('store.id', store.storeId);
    Sentry.setTag('store.name', store.storeName ?? 'unknown');
  } else {
    Sentry.setTag('store.id', 'none');
    Sentry.setTag('store.name', 'none');
  }
}

type Severity = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

export function captureException(
  err: unknown,
  context: Record<string, unknown> = {},
  level: Severity = 'error',
): void {
  const safeContext = { ...context };
  console.error('[ErrorTracking]', err, safeContext);
  if (!SENTRY_DSN) return;
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (safeContext.screen) scope.setTag('screen', String(safeContext.screen));
    if (safeContext.action) scope.setTag('action', String(safeContext.action));
    scope.setContext('extra', safeContext);
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
  });
}

export function captureMessage(message: string, level: Severity = 'info'): void {
  if (level === 'error' || level === 'warning') {
    console.warn('[ErrorTracking]', message);
  }
  if (!SENTRY_DSN) return;
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    Sentry.captureMessage(message);
  });
}

export function addBreadcrumb(
  message: string,
  data: Record<string, unknown> = {},
  category = 'app',
): void {
  if (!SENTRY_DSN) return;
  Sentry.addBreadcrumb({ message, category, data, level: 'info' });
}

/**
 * Lightweight transaction shim. Sentry's perf API differs across SDKs;
 * for now we just measure duration and emit a breadcrumb on finish so
 * the sync engine's `finishSyncTransaction(status)` contract holds.
 */
export function startTransaction(name: string, op: string): (status: string) => void {
  const startedAt = Date.now();
  return (status: string) => {
    const durationMs = Date.now() - startedAt;
    addBreadcrumb(`tx.${op}.${name}`, { status, durationMs }, 'performance');
  };
}

export interface SyncContext {
  isSyncing: boolean;
  lastSyncAt: string | null;
  syncError: string | null;
}

export function setSyncContext(ctx: SyncContext): void {
  if (!SENTRY_DSN) return;
  Sentry.setContext('sync', { ...ctx });
}

const ErrorTracking = {
  initErrorTracking,
  setUser,
  setStore,
  captureException,
  captureMessage,
  addBreadcrumb,
  startTransaction,
  setSyncContext,
};

export default ErrorTracking;
